import React, { useEffect, useMemo, useRef, useState } from 'react';
import { mod, seededRandom } from './index';

// Simon — repeat a colour sequence that grows by 1 each round. Both players
// see the same seed-derived sequence (so it's the same memory test). INT
// advantage slows the playback (more time to encode) and gives free preview
// rounds at the start (you see the first few uncontested).

const PADS    = 4;
const MAX_LEN = 20;     // max sequence length we generate; few people clear this
const COLOURS = ['#e07070', '#7ab8e0', '#5cb870', '#facc15'];

function paramsForMod(myMod, theirMod) {
  const diff = myMod - theirMod;
  // Equal/below: 600ms per flash, 0 free preview
  // +2 mod:      700ms,           1 free preview round
  // +4 mod:      800ms,           2 free preview rounds
  if (diff >= 4) return { flashMs: 800, freePreviews: 2 };
  if (diff >= 2) return { flashMs: 700, freePreviews: 1 };
  return            { flashMs: 600, freePreviews: 0 };
}

export default {
  id: 'simon',
  label: 'Simon',
  stat: 'int',
  icon: '🧠',
  description: 'Memorise the colour sequence and tap it back. Higher INT slows playback and grants free preview rounds.',
  paramsFromStats(myScore, theirScore) {
    return paramsForMod(mod(myScore), mod(theirScore));
  },
  Component({ challenge, mySide, params, seed, onScore }) {
    const flashMs       = params?.flashMs       || 600;
    const freePreviews  = params?.freePreviews  || 0;

    // Deterministic full sequence from seed.
    const fullSeq = useMemo(() => {
      const rng = seededRandom(seed | 0);
      const arr = [];
      for (let i = 0; i < MAX_LEN; i++) arr.push(Math.floor(rng() * PADS));
      return arr;
    }, [seed]);

    // Round = how long the sequence currently is. Start AFTER free previews
    // so low-INT side starts at 1, +2 INT mod starts at 2, +4 starts at 3.
    const startRound = freePreviews + 1;
    const [round,    setRound]    = useState(startRound);
    const [phase,    setPhase]    = useState('playing'); // 'playing' | 'input' | 'done'
    const [activeIdx,setActiveIdx]= useState(-1);        // currently flashing pad
    const [inputIdx, setInputIdx] = useState(0);
    const [score,    setScore]    = useState(startRound - 1); // longest cleared
    const submittedRef = useRef(false);

    // Run a "play sequence" timer effect when entering playing phase.
    useEffect(() => {
      if (phase !== 'playing') return;
      let cancelled = false;
      const seq = fullSeq.slice(0, round);
      let i = 0;
      const tick = () => {
        if (cancelled) return;
        if (i >= seq.length) {
          setActiveIdx(-1);
          setPhase('input');
          setInputIdx(0);
          return;
        }
        setActiveIdx(seq[i]);
        setTimeout(() => {
          if (cancelled) return;
          setActiveIdx(-1);
          setTimeout(() => {
            if (cancelled) return;
            i += 1;
            tick();
          }, Math.max(80, flashMs * 0.25));
        }, flashMs);
      };
      // Brief pre-roll before the flashes start so it doesn't surprise the player.
      const id = setTimeout(tick, 500);
      return () => { cancelled = true; clearTimeout(id); };
    }, [phase, round, fullSeq, flashMs]);

    const finish = (finalScore) => {
      if (submittedRef.current) return;
      submittedRef.current = true;
      setPhase('done');
      onScore(finalScore);
    };

    const handlePad = (i) => {
      if (phase !== 'input') return;
      const seq = fullSeq.slice(0, round);
      if (seq[inputIdx] !== i) {
        // Wrong — game over, score = round - 1 (last cleared length)
        finish(round - 1);
        return;
      }
      // Visual feedback for the tap
      setActiveIdx(i);
      setTimeout(() => setActiveIdx(-1), 120);
      const next = inputIdx + 1;
      if (next >= seq.length) {
        // Round cleared — bump score, advance round
        const cleared = round;
        setScore(cleared);
        if (cleared >= MAX_LEN) {
          finish(cleared);
        } else {
          setTimeout(() => {
            setRound(cleared + 1);
            setPhase('playing');
          }, 500);
        }
      } else {
        setInputIdx(next);
      }
    };

    return (
      <div style={styles.wrap}>
        <div style={styles.title}>Simon</div>
        <div style={styles.subtitle}>
          {phase === 'playing' && `Watch · sequence length ${round}`}
          {phase === 'input'   && `Repeat · ${inputIdx}/${round}`}
          {phase === 'done'    && `Final length: ${score}`}
        </div>
        {freePreviews > 0 && phase === 'playing' && round <= startRound && (
          <div style={styles.freeHint}>📚 Free preview round (INT bonus)</div>
        )}

        <div style={styles.padGrid}>
          {COLOURS.map((c, i) => {
            const lit = activeIdx === i;
            return (
              <button
                key={i}
                style={{
                  ...styles.pad,
                  background: lit ? c : `${c}30`,
                  borderColor: lit ? c : `${c}80`,
                  boxShadow: lit ? `0 0 24px ${c}` : 'none',
                  transition: 'all 90ms ease-out',
                }}
                onClick={() => handlePad(i)}
                disabled={phase !== 'input'}
              />
            );
          })}
        </div>

        <div style={styles.scoreLine}>Best round: {score}</div>
      </div>
    );
  },
};

const styles = {
  wrap:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 16 },
  title:     { fontSize: 22, fontWeight: 800, color: '#d4a847', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.06em' },
  subtitle:  { fontSize: 13, color: '#9a8b6e' },
  freeHint:  { fontSize: 11, color: '#7ab8e0', fontWeight: 700, letterSpacing: '0.04em' },
  padGrid:   { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, width: 'min(280px, 70vw)', aspectRatio: '1' },
  pad:       { borderRadius: 10, border: '3px solid', cursor: 'pointer', padding: 0 },
  scoreLine: { fontSize: 13, color: '#9a8b6e', fontFamily: "'Press Start 2P', monospace" },
};
